import { Check, ChevronDown, Globe } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAppLocale, locales, type AppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

interface LanguageSwitcherProps {
  collapsed?: boolean;
}

function getLocaleLabel(locale: AppLocale) {
  return locale === "es" ? m.language_spanish() : m.language_english();
}

export function LanguageSwitcher({ collapsed = false }: LanguageSwitcherProps) {
  const { locale, setLocale } = useAppLocale();

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title={m.language_switcher_label()}
            aria-label={m.language_switcher_label()}
            className="flex size-9 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-background hover:text-foreground"
          >
            <Globe className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" className="w-44">
          {locales.map((option) => (
            <DropdownMenuItem
              key={option}
              className="justify-between"
              onClick={() => setLocale(option)}
            >
              <span>{getLocaleLabel(option)}</span>
              {option === locale ? <Check className="size-4" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
      <Button
        aria-label={m.language_switcher_label()}
        variant="outline"
        size="sm"
        className="w-full justify-between"
      >
        <span className="flex items-center gap-2">
          <Globe className="size-4" />
          {getLocaleLabel(locale)}
        </span>
        <ChevronDown className="size-4 text-muted-foreground" />
      </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-48">
        {locales.map((option) => (
          <DropdownMenuItem
            key={option}
            className="justify-between"
            onClick={() => setLocale(option)}
          >
            <span>{getLocaleLabel(option)}</span>
            {option === locale ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
